import './BenchmarkingChartTable.scss';
import { useCallback, useMemo } from 'react';
import { Tercile } from '@blisspointmedia/bpm-types/dist/Benchmarking';
import { Formatter } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { SuperTopItemType } from '@Components/StickyTable/StickyTable';
import { SuperHeaderItem } from '@src/Components/MetricsTable/metrics-table-utils';
import { formatDateLabel } from '../../utils/date-utils';
import {
  DATE_PERIODS_TO_NOUN,
  formatPercent,
  renderWithPercentChangeOption,
  TERCILE_DATA_PRETTY_NAME_MAP,
  TransformedData
} from './benchmarkingConstants';

interface BenchmarkingChartTableViewProps {
  data: TransformedData;
  selectedMetric: string;
  selectedMetricPrettyName: string;
  tercileOptions: string[];
  activeFormatter: Formatter;
  companyName: string;
  tercilePrettyNameMap: Record<Tercile, string>;
  companyTercile: string;
  renderTercileMap: Partial<Record<Tercile, boolean>>;
  showPercentChange: boolean;
  selectedDatePeriod: string;
}

export const BenchmarkingChartTableView = ({
  data,
  selectedMetric,
  selectedMetricPrettyName,
  tercileOptions,
  activeFormatter,
  companyName,
  tercilePrettyNameMap,
  companyTercile,
  renderTercileMap,
  showPercentChange,
  selectedDatePeriod
}: BenchmarkingChartTableViewProps) => {
  const [headers, superHeaders] = useMemo(() => {
    const formatter = showPercentChange ? formatPercent : activeFormatter;
    const sHeaders: SuperTopItemType<SuperHeaderItem>[] = [
      { span: 2, data: { text: '', leftDivider: false, rightDivider: false } }
    ];
    const hdrs: Header[] = [
      {
        label: 'Date',
        name: 'date',
        width: 200,
        nonInteractive: true,
        renderer: item =>
          formatDateLabel(item.date, {}, DATE_PERIODS_TO_NOUN[selectedDatePeriod], false, {
            Week: 'MM/dd/yy'
          }) || '-'
      },
      {
        label: companyName,
        name: selectedMetric,
        nonInteractive: true,
        width: 200,
        renderer: renderWithPercentChangeOption(item => {
          const dataPoint: number = item[`${companyTercile}_company_${selectedMetric}`];
          return formatter(dataPoint);
        }, showPercentChange)
      }
    ];
    const getDividerClass = (leftDivider: boolean, rightDivider: boolean): string => {
      if (leftDivider) return 'left-div';
      if (rightDivider) return 'right-div';
      return '';
    };
    const makeHeader = (
      ter: string,
      type: string,
      leftDivider: boolean,
      rightDivider: boolean
    ): Header => ({
      label: TERCILE_DATA_PRETTY_NAME_MAP[type],
      name: `${ter}_${type}_${selectedMetric}`,
      nonInteractive: true,
      flex: 1,
      className: getDividerClass(leftDivider, rightDivider),
      renderer: renderWithPercentChangeOption(item => {
        const dataPoint = item[`${ter}_${type}_${selectedMetric}`];
        return formatter(dataPoint);
      }, showPercentChange)
    });
    let seenFirstTercile = false;
    tercileOptions.forEach(tercile => {
      if (renderTercileMap[tercile]) {
        const low = makeHeader(tercile, 'lower', seenFirstTercile, false);
        const median = makeHeader(tercile, 'median', false, false);
        const high = makeHeader(tercile, 'upper', false, true);
        hdrs.push(low, median, high);
        sHeaders.push({
          span: 3,
          data: {
            text: `${tercilePrettyNameMap[tercile]} ${selectedMetricPrettyName}`,
            leftDivider: true,
            rightDivider: true
          }
        });
        seenFirstTercile = true;
      }
    });
    return [hdrs, sHeaders];
  }, [
    selectedMetric,
    tercileOptions,
    companyName,
    activeFormatter,
    companyTercile,
    tercilePrettyNameMap,
    renderTercileMap,
    showPercentChange,
    selectedMetricPrettyName,
    selectedDatePeriod
  ]);

  const superHeadersRenderer = useCallback(({ style, classes, rowData }) => {
    classes.push('bpm-super-header');
    if (rowData.data.text) {
      classes.push('bpm-super-header-with-data');
    }
    return (
      <div style={style} className={classes.join(' ')}>
        {rowData.data.text}
      </div>
    );
  }, []);

  const headersRenderer = useCallback(({ data: headerData, columnIndex }) => {
    const classNames: string[] = [];
    if (columnIndex > 1) {
      classNames.push('spend-tercile-headers');
      const stringData = typeof headerData === 'string' ? headerData : '';
      if (columnIndex > 2 && stringData.startsWith(TERCILE_DATA_PRETTY_NAME_MAP.lower)) {
        classNames.push('left-div');
      } else if (stringData.startsWith(TERCILE_DATA_PRETTY_NAME_MAP.upper)) {
        classNames.push('right-div');
      }
    }
    return <div className={classNames.join(' ')}>{headerData}</div>;
  }, []);

  return (
    <BPMTable
      className='cross-client-benchmarking-table'
      data={data}
      superHeaders={superHeaders}
      superHeadersRenderer={superHeadersRenderer}
      headers={headers}
      filterBar={false}
      headerHeight={40}
      superHeaderHeight={60}
      headersRenderer={headersRenderer}
      bottomRowHeight={30}
      cellClassName='x-benchmarking-table-cell'
    />
  );
};
